import { useCallback } from 'react'
import { RColumn, RemoteTable } from 'medere-ui-framework'
import { Button } from 'primereact/button'
import {
  ContractOperationLogDetailDTO,
  ContractOperationLogItemDTO,
} from '../../dto/contractOperationLog/ContractOperationLogDTO'
import { getContractOperationLogDetails } from '../../services/ContractOperationLogController'
import { parseApiError } from '../../services/apiError'
import useNotify from '../../hooks/useNotify'
import NvTooltips from '../../components/novarium/NvTooltips'
import { describeCounts, EMPTY, formatDate, formatDateTime } from './contractOperationLogFormat'

interface ContractOperationLogDetailViewProps {
  operation: ContractOperationLogItemDTO
  onBack: () => void
}

type Row = ContractOperationLogDetailDTO
const text = (value: string | null) => value || EMPTY
const truncated = (value: string | null) => (
  <span className='nv-truncate nv-tip' data-pr-tooltip={value ?? undefined}>{text(value)}</span>
)
const CENTER = 'contract-operation-log__center text-center'
const codeBody = (row: Row) => text(row.practiceCode)

function Validity({ start, end }: Readonly<{ start: string | null; end: string | null }>) {
  if (!start && !end) return <>{EMPTY}</>
  return (
    <span className='contract-operation-log__validity'>
      <span>{formatDate(start)}</span>
      <span>-</span>
      <span>{end ? formatDate(end) : 'sin fin'}</span>
    </span>
  )
}

const attemptedBody = (row: Row) => <Validity start={row.attemptedStart} end={row.attemptedEnd} />
const conflictBody = (row: Row) => <Validity start={row.conflictStart} end={row.conflictEnd} />
const reasonBody = (row: Row) => truncated(row.reason)
const healthEntityBody = (row: Row) => truncated(row.healthEntityName)
const planBody = (row: Row) => truncated(row.healthEntityPlanName)
const practiceBody = (row: Row) => truncated(row.practiceDescription)
const RESULT_STYLE = { width: 260 }
const CODE_STYLE = { width: 110 }
const VALIDITY_STYLE = { width: 210 }

function ReadOnlyField({ label, value, className = 'nv-w-m' }: Readonly<{ label: string; value: string; className?: string }>) {
  return (
    <div className={`nv-ro ${className}`}>
      <span className='nv-ro-label'>{label}</span>
      <span className='nv-ro-value'>{value}</span>
    </div>
  )
}

export default function ContractOperationLogDetailView({ operation, onBack }: Readonly<ContractOperationLogDetailViewProps>) {
  const notify = useNotify()

  const loadPage = useCallback((offset: number, pageSize: number) => (
    getContractOperationLogDetails(operation.id, offset, pageSize).catch((error: unknown) => {
      const apiError = parseApiError(error)
      if (apiError.errorCode === 'CONTRACT_OPERATION_LOG_NOT_FOUND') {
        notify.error(apiError.message)
        onBack()
      } else {
        notify.error('No se pudo cargar el detalle de la operación. Intentá de nuevo.')
      }
      return [] as Row[]
    })
  ), [operation.id, notify, onBack])

  return (
    <div className='nv-scope nv-page contract-operation-log'>
      <NvTooltips />
      <header className='nv-page-head'>
        <Button type='button' link icon='pi pi-angle-double-left' label='Volver' onClick={onBack} />
        <h1 className='nv-h1'>Historial de operaciones</h1>
        <span className='nv-subtitle'>
          {operation.operationTypeLabel} · {formatDateTime(operation.startedAt)}
        </span>
      </header>

      <section className='nv-section' aria-labelledby='contract-operation-log-data'>
        <h2 className='nv-section-title' id='contract-operation-log-data'>Operación</h2>
        <div className='nv-form-row'>
          <ReadOnlyField label='Fecha y hora' value={formatDateTime(operation.startedAt)} className='nv-w-s' />
          <ReadOnlyField label='Usuario' value={text(operation.userName)} />
          <ReadOnlyField label='Estado' value={operation.statusLabel} className='nv-w-s' />
          <ReadOnlyField label='Contratos' value={describeCounts(operation)} className='nv-w-s' />
        </div>
        <ReadOnlyField label='Parámetros' value={text(operation.description)} className='nv-w-l' />
      </section>

      <section className='nv-section contract-operation-log__table' aria-labelledby='contract-operation-log-contracts'>
        <h2 className='nv-section-title' id='contract-operation-log-contracts'>Contratos</h2>
        {operation.operationType === 'EXCEL_IMPORT' && (
          <p className='nv-note'>
            <i className='pi pi-info-circle' aria-hidden='true' />
            De una importación, los contratos creados, actualizados y cerrados figuran solo en el total; acá están las
            filas omitidas del archivo y los contratos vigentes que no estaban en él.
          </p>
        )}
        <RemoteTable
          idTable='contractOperationLogDetail'
          dataKey='id'
          getItems={loadPage}
          filterRow={false}
          tableMinWidth={1500}
          notFoundText='Esta operación no tiene contratos registrados.'
        >
          <RColumn field='resultLabel' header='Resultado' style={RESULT_STYLE} />
          <RColumn field='practiceCode' header='Código' className={CENTER} headerClassName={CENTER}
            style={CODE_STYLE} body={codeBody} />
          <RColumn field='practiceDescription' header='Práctica' grow={2} body={practiceBody} />
          <RColumn field='attemptedStart' header='Vigencia aplicada' className={CENTER} headerClassName={CENTER}
            style={VALIDITY_STYLE} body={attemptedBody} />
          <RColumn field='conflictStart' header='Se solapa con' className={CENTER} headerClassName={CENTER}
            style={VALIDITY_STYLE} body={conflictBody} />
          <RColumn field='reason' header='Motivo' grow={2} body={reasonBody} />
          <RColumn field='healthEntityName' header='Obra social' body={healthEntityBody} />
          <RColumn field='healthEntityPlanName' header='Plan' body={planBody} />
        </RemoteTable>
      </section>
    </div>
  )
}
